import React, { useCallback, useEffect, useRef, useState } from "react";
import "./courses-filter.component.scoped.scss";
import useOutsideAlerter from "../../../../../hooks/useOutisdeAlert";
import { Box, IconButton, InputAdornment, TextField } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { useTranslation } from "react-i18next";
import ArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import ArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import { services } from "../../../../../services";
import AppFilterChipComponent from "../../../../../components/form/app-filter-chip";
import {
  CompletionStatus,
  CompletionStatusLabel,
  completionStatusLabels,
  CourseFilters,
} from "../../../types/courses.types";
import AppSwitchComponent from "../../../../../components/form/app-switch";
import AppCheckboxComponent from "../../../../../components/form/app-checkbox";
import AppButtonComponent from "../../../../../components/form/app-button";

const CoursesFilterComponent: React.FC<{
  onFilterChange: (filters: CourseFilters) => void;
}> = ({ onFilterChange }) => {
  const { t } = useTranslation();
  const dropDownFilterRefs = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [courseCategories, setCourseCategories] = useState<any[]>([]);
  const [dropdownFilter, setDropdownFilter] = useState(false);

  // Filter state
  const [searchInput, setSearchInput] = useState<string>("");
  const [filters, setFilters] = useState<CourseFilters>({
    categoryIds: [],
    searchText: "",
    status: null, // null = all courses
    completionStatus: CompletionStatus.ALL,
  });

  // Temporary category filter state (before submit)
  const [tempCategoryFilter, setTempCategoryFilter] = useState<{
    [key: number]: boolean;
  }>({});

  /**
   * Fetch course categories
   */
  const fetchCourseCategories = useCallback(async () => {
    try {
      const response = await services.lookups.listCourseCategories();
      setCourseCategories(response || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, []);

  useEffect(() => {
    fetchCourseCategories();
  }, [fetchCourseCategories]);

  /**
   * Handle category checkbox change
   */
  const onCategoryFilterChange = useCallback(
    (categoryId: number, checked: boolean) => {
      setTempCategoryFilter((prev) => ({
        ...prev,
        [categoryId]: checked,
      }));
    },
    [],
  );

  /**
   * Submit category filter
   */
  const onSubmitCategoryFilter = useCallback(() => {
    const selectedCategoryIds = Object.keys(tempCategoryFilter)
      .filter((key) => tempCategoryFilter[Number(key)])
      .map(Number);

    const newFilters = {
      ...filters,
      categoryIds: selectedCategoryIds,
    };
    setFilters(newFilters);
    onFilterChange(newFilters);
    setDropdownFilter(false);
  }, [tempCategoryFilter, filters, onFilterChange]);

  /**
   * Reset category filter
   */
  const onResetCategoryFilter = useCallback(() => {
    setTempCategoryFilter({});
    const newFilters = {
      ...filters,
      categoryIds: [],
    };
    setFilters(newFilters);
    onFilterChange(newFilters);
  }, [filters, onFilterChange]);

  /**
   * Handle search text change with debouncing
   */
  const onSearchChange = useCallback(
    (searchText: string) => {
      // Update input immediately for responsive UI
      setSearchInput(searchText);

      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout to update filters after user stops typing (600ms)
      searchTimeoutRef.current = setTimeout(() => {
        const newFilters = {
          ...filters,
          searchText,
        };
        setFilters(newFilters);
        onFilterChange(newFilters);
      }, 600);
    },
    [filters, onFilterChange],
  );

  /**
   * Cleanup timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Sync searchInput with filters.searchText on initial load
   */
  useEffect(() => {
    setSearchInput(filters.searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handle status switch change
   */
  const onStatusChange = useCallback(
    (checked: boolean) => {
      const newFilters = {
        ...filters,
        status: checked ? true : null, // true = active only, null = all
      };
      setFilters(newFilters);
      onFilterChange(newFilters);
    },
    [filters, onFilterChange],
  );

  /**
   * Handle completion status chip click
   */
  const onCompletionStatusChange = useCallback(
    (status: CompletionStatus) => {
      const newFilters = {
        ...filters,
        completionStatus: status,
      };
      setFilters(newFilters);
      onFilterChange(newFilters);
    },
    [filters, onFilterChange],
  );

  /**
   * Close dropdown when clicking outside
   */
  useOutsideAlerter(dropDownFilterRefs, () => {
    setDropdownFilter(false);
  });

  /**
   * Initialize temp category filter when dropdown opens
   */
  useEffect(() => {
    if (dropdownFilter) {
      const initialTempFilter: { [key: number]: boolean } = {};
      filters.categoryIds.forEach((id) => {
        initialTempFilter[id] = true;
      });
      setTempCategoryFilter(initialTempFilter);
    }
  }, [dropdownFilter, filters.categoryIds]);

  return (
    <Box className="filter__content">
      <Box className="d-flex align-items-center" style={{ gap: "1rem" }}>
        {completionStatusLabels.map((label: CompletionStatusLabel) => (
          <AppFilterChipComponent
            key={label.value}
            label={t(label.label as string)}
            size="small"
            selected={filters.completionStatus === label.value}
            onClick={() => onCompletionStatusChange(label.value)}
          />
        ))}
      </Box>
      <Box className="filter__content__inner">
        <AppSwitchComponent
          label={t("EDUCATION_PAGE.ACTIVE_COURSES")}
          size="small"
          color="primary"
          onChange={(e: any) => onStatusChange(e.target?.checked ?? false)}
          name="status"
          checked={filters.status === true}
        />
        <Box className="standard_text_input w-100" display="flex">
          <TextField
            variant="standard"
            type="text"
            autoComplete="off"
            onChange={(e: any) => onSearchChange(e.target.value)}
            value={searchInput}
            placeholder={t("EDUCATION_PAGE.SEARCH_COURSE")}
            className="w-100"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <div className="dropdown-filter" ref={dropDownFilterRefs}>
          <Box
            className="filter-button"
            onClick={() => setDropdownFilter(!dropdownFilter)}
          >
            {t("EDUCATION_PAGE.ALL_CATEGORIES")}
            {dropdownFilter ? <ArrowUpIcon /> : <ArrowDownIcon />}
          </Box>

          {dropdownFilter && (
            <Box className="filter-menu global__box__content">
              <ul className="p-0 m-0">
                {courseCategories.map((category, index) => {
                  return (
                    <li key={category.id || index}>
                      <AppCheckboxComponent
                        label={t(category.name) || category.name}
                        size="small"
                        checked={tempCategoryFilter[category.id] || false}
                        onChange={(e: any) =>
                          onCategoryFilterChange(category.id, e.target.checked)
                        }
                      />
                    </li>
                  );
                })}
              </ul>
              <hr
                style={{
                  width: "219px",
                  marginLeft: "-0.5rem",
                }}
              />
              <Box className="filter-actions d-flex mt-3 mb-2 align-items-center justify-content-between">
                <AppButtonComponent
                  variant="outlined"
                  size="small"
                  color="secondary"
                  onClick={onResetCategoryFilter}
                >
                  {t("RESET")}
                </AppButtonComponent>
                <AppButtonComponent
                  variant="contained"
                  size="small"
                  color="primary"
                  onClick={onSubmitCategoryFilter}
                >
                  {t("SUBMIT")}
                </AppButtonComponent>
              </Box>
            </Box>
          )}
        </div>
      </Box>
    </Box>
  );
};

export default CoursesFilterComponent;
